import React, { forwardRef, useState } from 'react';
import { Typography } from '../Typography';
import styles from './Radio.module.scss';

export interface RadioProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size' | 'type' | 'onChange'> {
  label?: React.ReactNode;
  checked?: boolean;
  defaultChecked?: boolean;
  onChange?: (checked: boolean) => void;
  disabled?: boolean;
  size?: 'sm' | 'md' | 'lg';
  color?: 'primary' | 'secondary' | 'success' | 'warning' | 'error';
  labelClassName?: string;
  error?: string;
}

const Radio = forwardRef<HTMLInputElement, RadioProps>(
  (
    {
      label,
      checked,
      defaultChecked = false,
      onChange,
      disabled = false,
      size = 'md',
      color = 'primary',
      className = '',
      labelClassName = '',
      error,
      ...props
    },
    ref
  ) => {
    const [isChecked, setIsChecked] = useState(defaultChecked);
    const [isFocused, setIsFocused] = useState(false);

    const currentChecked = checked !== undefined ? checked : isChecked;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newChecked = e.target.checked;

      if (checked === undefined) {
        setIsChecked(newChecked);
      }

      onChange?.(newChecked);
    };

    const handleFocus = () => {
      setIsFocused(true);
    };

    const handleBlur = () => {
      setIsFocused(false);
    };

    const radioClasses = [
      styles.radio,
      styles[`size-${size}`],
      styles[`color-${color}`],
      currentChecked && styles.checked,
      disabled && styles.disabled,
      isFocused && styles.focused,
      error && styles.error,
      className,
    ]
      .filter(Boolean)
      .join(' ');

    const labelClasses = [
      styles.label,
      disabled && styles.labelDisabled,
      error && styles.labelError,
      labelClassName,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div className={styles.radioContainer}>
        <label className={radioClasses}>
          <input
            ref={ref}
            type="radio"
            checked={currentChecked}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            disabled={disabled}
            className={styles.input}
            id={`radio-${Math.random().toString(36).substr(2, 9)}`}
            {...props}
          />

          <div
            className={styles.radioCircle}
            style={error ? { borderColor: 'var(--color-error)' } : {}}
          >
            {currentChecked && <div className={styles.radioDot} />}
          </div>
        </label>

        {label && (
          <label
            htmlFor={props.id || `radio-${Math.random().toString(36).substr(2, 9)}`}
            className={labelClasses}
          >
            <Typography
              variant="text-sm"
              as="span"
              color={disabled ? 'disabled' : error ? 'error' : 'secondary'}
              weight="normal"
            >
              {label}
            </Typography>
          </label>
        )}
      </div>
    );
  }
);

Radio.displayName = 'Radio';

export default Radio;
